import { Agent } from "app-types/agent";

/**
 * Navigation utilities for agent-aware routing
 */
export class AgentNavigation {
  /**
   * Generate URL for agent chat (new conversation)
   */
  static getAgentChatUrl(agentId: string): string {
    return `/agent/${agentId}`;
  }

  /**
   * Generate URL for specific agent thread
   */
  static getAgentThreadUrl(agentId: string, threadId: string): string {
    return `/agent/${agentId}/${threadId}`;
  }

  /**
   * Get main chat URL
   */
  static getMainChatUrl(): string {
    return "/";
  }

  /**
   * Get main chat thread URL
   */
  static getMainChatThreadUrl(threadId: string): string {
    return `/chat/${threadId}`;
  }

  /**
   * Parse agent context from current pathname
   */
  static parseAgentContext(pathname: string): {
    isAgentChat: boolean;
    agentId?: string;
    threadId?: string;
  } {
    const agentMatch = pathname.match(/^\/agent\/([^\/]+)(?:\/([^\/]+))?$/);

    if (agentMatch) {
      return {
        isAgentChat: true,
        agentId: agentMatch[1],
        threadId: agentMatch[2],
      };
    }

    return {
      isAgentChat: false,
    };
  }

  /**
   * Check if current path is an agent chat
   */
  static isAgentChatPath(pathname: string): boolean {
    return pathname.startsWith("/agent/");
  }

  /**
   * Check if current path is main chat
   */
  static isMainChatPath(pathname: string): boolean {
    return pathname === "/" || pathname.startsWith("/chat/");
  }

  /**
   * Get navigation breadcrumbs for current path
   */
  static getBreadcrumbs(
    pathname: string,
    agent?: Agent,
  ): Array<{
    label: string;
    href: string;
    isActive: boolean;
  }> {
    const context = this.parseAgentContext(pathname);
    const breadcrumbs = [];

    // Main chat breadcrumb
    breadcrumbs.push({
      label: "Main Chat",
      href: this.getMainChatUrl(),
      isActive: !context.isAgentChat,
    });

    // Agent breadcrumb if in agent context
    if (context.isAgentChat && agent) {
      breadcrumbs.push({
        label: agent.name,
        href: this.getAgentChatUrl(agent.id),
        isActive: true,
      });
    }

    return breadcrumbs;
  }

  /**
   * Generate agent-specific URL parameters for API calls
   */
  static getAgentUrlParams(agent?: Agent): URLSearchParams {
    const params = new URLSearchParams();

    if (agent) {
      params.set("agentId", agent.id);
    }

    return params;
  }
}
